import * as Sentry from '@sentry/vue'
import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { getConfiguredBackendUrl } from './runtimeEnv'
import { clearAccessToken, getAccessToken } from './tokenStorage'

// Create the axios instance
const backendUrl = getConfiguredBackendUrl()
const axiosInstance = axios.create(backendUrl ? { baseURL: backendUrl } : {})

function clearAuthTokens(): void {
    clearAccessToken()
}

function redirectToLogin(currentPath: string): void {
    const authRoutes = [
        '/router/login',
        '/router/login-with-code',
        '/router/sso',
        '/router/sso/callback',
        '/router/saml2-error',
        '/router/forgot-password',
        '/router/reset-password',
    ]

    // Skip redirect if already on auth routes
    if (!authRoutes.some(route => currentPath.startsWith(route))) {
        // Store the intended URL so we can redirect user to it after login
        const fullPath = window.location.pathname + window.location.search + window.location.hash
        sessionStorage.setItem('login-redirect-url', fullPath)
        window.location.replace('/router/login')
    }
}

// Add request interceptor to the instance
axiosInstance.interceptors.request.use(
    (config) => {
        const accessToken = getAccessToken()
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    },
)

// Add response interceptors to the instance
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const status = error.response?.status
        const ignoredStatuses = [401, 403, 419, 422, 429]

        if (!status || !ignoredStatuses.includes(status)) {
            Sentry.setTag('source', 'axios')
            Sentry.captureException(error)
        }

        // 401 Unauthorized errors
        if (error.response && error.response.status === 401) {
            const currentPath = window.location.pathname

            // Skip if user is on surveys routes
            if (currentPath.startsWith('/surveys')) {
                if (currentPath !== '/surveys/recipients/login') {
                    window.location.href = '/surveys/recipients/login'
                }
                return Promise.reject(error)
            }
            clearAuthTokens()
            redirectToLogin(currentPath)
            return Promise.reject(error)
        }

        return Promise.reject(error)
    },
)

export default axiosInstance
